import { Injectable } from '@angular/core';
import { TreeItem, HContent, TreeLevel, HComponent, HSubComponent, HSubComponentElement, HElement, HRoute, ComponentType, Overrides, RouteOverride, ComponentOverride, ElementOverride } from './tree.model';

@Injectable({
  providedIn: 'root'
})
export class TreeDataService {

  videos: Map<string, string>;

  constructor() { }

  private normalizeProperties<T extends { properties?: any }>(node: T): T {
    node.properties = node.properties && !Array.isArray(node.properties) ? node.properties : {};
    return node;
  }

  buildElements(level: TreeLevel, elements: HElement[]): TreeItem[];
  buildElements(level: TreeLevel, elements: HSubComponentElement[]): TreeItem[];

  buildElements(level: TreeLevel, elements: HElement[] | HSubComponentElement[]) {
    if (!elements) {
      return [];
    }

    const items: TreeItem[] = [];
    for (const element of elements) {
      const id = element.elementId || `gen-${level}-${Math.random().toString(36).substr(2, 9)}`;
      const type = element.type;

      if( !type ){
        continue
      }

      this.normalizeProperties(element);
      const item: TreeItem = {
        id: id,
        fromId: element.fromId,
        position: element.position,
        children: [],
        tag: type.toString(),
        name: element.value ? element.value : "",
        level: level,
        expanded: false,
        item: element
      };
      items.push(item);
    }
    return items;
  }

  buildSubComponents(subComponents: HSubComponent[]) {
    if (!subComponents) {
      return [];
    }

    const items: TreeItem[] = [];
    for (const subComponent of subComponents) {
      const id = subComponent.componentId || `gen-${TreeLevel.SubComponent}-${Math.random().toString(36).substr(2, 9)}`;
      const type = subComponent.type;
      const elements = this.buildElements(TreeLevel.SubComponentElement, subComponent.elements);
      this.normalizeProperties(subComponent);
      const item: TreeItem = {
        id: id,
        fromId: subComponent.fromId,
        position: subComponent.position,
        parentComponentId: subComponent.parentComponentId,
        children: [...elements],
        name: '',
        tag: type.toString(),
        level: TreeLevel.SubComponent,
        expanded: false,
        item: subComponent
      };
      items.push(item);
    }
    return items;
  }


  private isSubComponentElementType(type: string): boolean {
    return type === 'rating-component';
  }

  private buildSubComponentsFromElements(elements: HElement[]): TreeItem[] {
    if (!elements) return [];
    return elements.map(element => {
      this.normalizeProperties(element);
      return {
        id: element.elementId || `gen-${TreeLevel.SubComponent}-${Math.random().toString(36).substr(2, 9)}`,
        fromId: element.fromId,
        position: element.position,
        children: [] as TreeItem[],
        name: '',
        tag: element.type.toString(),
        level: TreeLevel.SubComponent,
        expanded: false,
        item: element,
        updatedProperties: element.properties
      };
    });
  }

  buildComponents(components: HComponent[]) {
    if (!components) {
      return [];
    }

    const items: TreeItem[] = [];
    for (const component of components) {
      const id = component.componentId || `gen-${TreeLevel.Component}-${Math.random().toString(36).substr(2, 9)}`;
      const type = component.type;
      const subComponents = this.buildSubComponents(component.subComponents);
      const allElements = (component.elements ?? []).filter(e => e != null);
      const realElements = allElements.filter(e => !this.isSubComponentElementType(e.type?.toString()));
      const subComponentElements = allElements.filter(e => this.isSubComponentElementType(e.type?.toString()));
      const elements = this.buildElements(TreeLevel.Element, realElements);
      const subComponentsFromElements = this.buildSubComponentsFromElements(subComponentElements);
      this.normalizeProperties(component);
      const item: TreeItem = {
        id: id,
        fromId: component.fromId,
        position: component.position,
        children: [...elements, ...subComponents, ...subComponentsFromElements],
        name: this.getComponentTopElementValue(component),
        level: TreeLevel.Component,
        tag: type.toString(),
        expanded: false,
        item: component
      };
      items.push(item);
    }
    return items;
  }
  getComponentTreeItemTopElementValue(treeItem: TreeItem) {
    for (const child of treeItem.children) {
      if (child.level == TreeLevel.Element) {
        return child.name;
      }
    }
    return '';
  }

  getRouteTreeItemTopElementValue(treeItem: TreeItem) {
    for (const child of treeItem.children) {
      if (child.level == TreeLevel.Component) {
        return child.name;
      }
    }
    return '';
  }


  getComponentTopElementValue(component: HComponent) {
    const elements = component.elements ?? [];

    for (const element of elements) {
      return element.value?.length > 80 ? element.value.substring(0, 80) + ' ...' : element.value;
    }
    return '';
  }

  getRouteTopElementValue(route: HRoute) {

    const components = route.components ?? [];

    for (const component of components) {
      const subComponents = component.subComponents ?? [];


      if (component?.properties?.vimeoId) {
        const id = component.properties.vimeoId || '';
        const video = this.videos?.get(id);
        if (video) {
          return video;
        }else{
          return "Unknown video"
        }
      }


      for (const subComponent of subComponents) {
        const elements = subComponent.elements ?? [];

        for (const element of elements) {
          return element.value?.length > 80 ? element.value.substring(0, 80) + ' ...' : element.value;
        }
      }

      const elements = component.elements ?? [];

      for (const element of elements) {
        return element.value?.length > 80 ? element.value.substring(0, 80) + ' ...' : element.value;
      }
    }
    return '';
  }

  buildRoutes(sections: HRoute[]) {
    const items: TreeItem[] = [];

    for (const section of sections) {
      const id = section.routeId || `gen-${TreeLevel.Route}-${Math.random().toString(36).substr(2, 9)}`;
      const name = this.getRouteTopElementValue(section);
      const components = this.buildComponents(section.components);
      this.normalizeProperties(section);
      const item: TreeItem = {
        id: id,
        fromId: id,
        position: section.position,
        name: name,
        children: [...components],
        level: TreeLevel.Route,
        tag: section.route,
        expanded: false,
        item: section,
      };
      items.push(item);
    }

    return items;
  }

  loadTraining(hContent: HContent, videos: Map<string, string>) {
    // build tree from hContent
    const items: TreeItem[] = [];
    this.videos = videos;

    console.log("Building routes from hContent.sections", hContent.sections)
    return this.buildRoutes(hContent.sections);

  }

  processRouteOverride(routes: TreeItem[], override: RouteOverride) {

    const theRoute = routes.find(route => route.id == override.routeId);

    if (override.added) {
      const route: TreeItem = {
        id: override.routeId,
        position: override.position,
        fromId: override.fromId,
        name: override.route,
        isNew: true,
        level: TreeLevel.Route,
        tag: override.route,
        updatedProperties: override.properties,
        expanded: false,
        item: null,
        children: []
      };
      routes.push(route)
      return
    }
    if (!theRoute) {
      console.error("Route not found: " + override.routeId);
      return;
    }
    if (override.removed) {
      theRoute.isDeleted = true;
    }
    if (override.moved) {
      theRoute.isMoved = true;
      theRoute.position = override.position;
    }
    if (override.properties) {
      theRoute.updatedProperties = override.properties;
    }
    routes.sort((a, b) => a.position - b.position);
  }

  findComponent(routes: TreeItem[], id: string): TreeItem | null {
    for (const route of routes) {
      for (const component of route.children) {
        if (component.id == id) {
          return component;
        }
        for (const subComponent of component.children) {
          if (subComponent.id == id) {
            return subComponent;
          }
        }
      }
    }
    return null;
  }

  findElement(routes: TreeItem[], id: string): TreeItem | null {
    for (const route of routes) {
      for (const component of route.children) {
        for (const item of component.children) {
          if (item.id == id) {
            return item;
          }
          if (item.children) {
            const found = this.findElement(item.children, id);
            if (found) {
              return found;
            }
          }
        }
      }
    }
    return null;
  }

  findComponentParent(routes: TreeItem[], override: ComponentOverride): TreeItem {

    const routeId = override.routeId
    const parentComponentId = override.parentComponentId
    let parent = null
    if (routeId) {
      const route = routes.find(route => route.id == routeId);
      if (route) {
        parent = route;
      }
    }
    if (parentComponentId) {
      const component = this.findComponent(routes, override.parentComponentId);
      if (component) {
        parent = component;
      } else {
        console.error("Component not found: " + override.parentComponentId);
      }
    }
    return parent;
  }

  processComponentOverride(routes: TreeItem[], override: ComponentOverride) {

    const parent = this.findComponentParent(routes, override);

    if (override.added) {
      if (parent) {
        parent.children.push({
          id: override.componentId,
          fromId: override.fromId,
          position: override.position,
          isNew: true,
          name: override.type,
          level: override.parentComponentId ? TreeLevel.SubComponent : TreeLevel.Component,
          tag: override.type,
          updatedProperties: override.properties,
          expanded: false,
          item: null,
          children: []
        })
      } else {
        console.error("Parent for component not found: " + (override.parentComponentId || override.routeId));
      }
    }
    const component = this.findComponent(routes, override.componentId);
    if (!component) {
      console.error("Component not found: " + override.componentId);
      return;
    }

    if (override.removed) {
      component.isDeleted = true;
    }
    if (override.position != undefined) {
      component.isMoved = true;
      component.updatedPosition = override.position;
    }
    if (override.properties) {
      component.updatedProperties = override.properties;
    }

    parent.children.sort((a, b) => a.position - b.position);
  }

  processElementOverride(routes: TreeItem[], override: ElementOverride) {

    const componentId = override.componentId;
    const component = this.findComponent(routes, componentId);
    if (!component) {
      console.error("Component not found: " + componentId);
      return;
    }
    if (override.added) {
      component.children.push({
        id: override.elementId,
        position: override.position,
        fromId: override.fromId,
        name: override.value,
        updatedText: override.updatedValue,
        isNew: true,
        level: TreeLevel.Element,
        tag: override.type,
        updatedProperties: override.properties,
        expanded: false,
        item: null,
        children: []
      })
    }

    const element = this.findElement(routes, override.elementId)
    if (!element) {
      console.warn("Could not find element %o", override.id)
      return
    }

    if (override.updatedValue) {
      element.isUpdated = true;
      element.updatedText = override.updatedValue;
      element.name = override.value;
    }
    if (override.removed) {
      element.isDeleted = true;
    }
    if (override.position != undefined) {
      element.isMoved = true;
      element.updatedPosition = override.position;
    }
    if (override.properties) {
      element.updatedProperties = override.properties;
    }
    console.log(element)

    component.children.sort((a, b) => a.position - b.position);
  }

  setProperties(tree: TreeItem[]) {
    for (const item of tree) {
      item.updatedProperties = item.item.properties;
      for (const child of item.children) {
        this.setProperties([child]);
      }
    }
  }


  resetNewFlags(tree: TreeItem[]) {
    let index = 0;
    for (const item of tree) {
      item.position = index;
      index++;
      if (item.isNew) {
        item.isNew = false;
      }
      for (const child of item.children) {
        this.resetNewFlags([child]);
      }
    }
  }

  processOverrides(tree: TreeItem[], overrides: Overrides[]) {
    if (overrides['routeOverrides']) {
      for (const override of (overrides['routeOverrides'] as RouteOverride[])) {
        this.processRouteOverride(tree, override);
      }
    }
    if (overrides['componentOverrides']) {
      for (const override of overrides['componentOverrides']) {
        this.processComponentOverride(tree, override);
      }
    }
    if (overrides['elementOverrides']) {
      for (const override of overrides['elementOverrides']) {
        this.processElementOverride(tree, override);
      }
    }

    // now have to update route name, component name, sub component name to the top element
    for (const route of tree) {
      for (const component of route.children) {
        for (const child of component.children) {
          if (child.level == TreeLevel.SubComponentElement) {
            child.name = child.children[0].updatedText ? child.children[0].updatedText : child.children[0].name
          }
          if (child.level == TreeLevel.Element) {
            component.name = child.updatedText ? child.updatedText : child.name
            break
          }
        }
      }
      route.name = route.children[0].name
    }

    console.log('overrides processed: ', overrides)
  }

  generateOverrides(tree: TreeItem[]) {

    const routeOverrides = []
    const componentOverrides = []
    const elementOverrides = []


    let routePosition = 0
    for (const route of tree) {
      const routeId = route.id
      if (route.isNew || route.isDeleted || route.isMoved || route.updatedProperties) {
        routeOverrides.push({
          routeId: route.id,
          fromId: route.fromId,
          added: route.isNew,
          moved: route.isMoved || route.isNew,
          position: route.updatedPosition ?? routePosition++,
          properties: route.updatedProperties,
          route: route.tag,
          removed: route.isDeleted
        })
      }
      const components = route.children;
      for (const component of components) {
        const componentId = component.id
        if (component.isNew || component.isDeleted || component.isMoved || component.updatedProperties) {
          componentOverrides.push({
            componentId: component.id,
            routeId,
            fromId: component.fromId,
            moved: component.isMoved || component.isNew,
            position: component.updatedPosition,
            added: component.isNew,
            properties: component.updatedProperties,
            type: component.tag,
            removed: component.isDeleted
          })
        }
        const children = component.children;
        for (const child of children) {
          if (child.level == 2) {
            // element
            if (child.isNew || child.isDeleted || child.isUpdated || child.isMoved || child.updatedProperties) {
              elementOverrides.push({
                elementId: child.id,
                componentId,
                fromId: child.fromId,
                routeId,
                moved: child.isMoved || child.isNew,
                value: child.name,
                isUpdated: child.isUpdated,
                position: child.updatedPosition,
                properties: child.updatedProperties,
                updatedValue: child.isNew ? child.name : child.updatedText,
                added: child.isNew,
                type: child.tag,
                removed: child.isDeleted
              })
            }
          }
          if (child.level == 3) {
            // subcomponent - uses componentOverrides with parentComponentId
            if (child.isNew || child.isDeleted || child.isUpdated || child.isMoved || child.updatedProperties) {
              componentOverrides.push({
                componentId: child.id,
                fromId: child.fromId,
                parentComponentId: componentId,
                moved: child.isMoved || child.isNew,
                position: child.updatedPosition,
                properties: child.updatedProperties,
                added: child.isNew,
                type: child.tag || child.name,
                removed: child.isDeleted
              })
            }
          }
          for (const subElement of child.children) {
            if (subElement.isNew || subElement.isDeleted || subElement.isUpdated || subElement.isMoved || subElement.updatedProperties) {
              elementOverrides.push({
                id: subElement.id,
                elementId: subElement.id,
                fromId: subElement.fromId,
                subComponentId: child.id,
                routeId,
                isUpdated: subElement.isUpdated,
                value: subElement.name,
                position: subElement.updatedPosition,
                properties: subElement.updatedProperties,
                updatedValue: subElement.isNew ? subElement.name : subElement.updatedText,
                added: subElement.isNew,
                moved: subElement.isMoved || subElement.isNew,
                type: subElement.tag,
                removed: subElement.isDeleted
              })
            }
          }
        }
      }
    }

    // Guard against emitting malformed overrides that carry only a position and
    // no type/route. These can arise when items are removed and positions are
    // recalculated across the whole tree (updatePositionsRecursive flags such an
    // item as moved), producing an override with a position but no identifying
    // type. Persisting it results in an element with no type, which then fails
    // to load. Strip these before sending to the backend.
    const validRouteOverrides = routeOverrides.filter(o => o.route);
    const validComponentOverrides = componentOverrides.filter(o => o.type);
    const validElementOverrides = elementOverrides.filter(o => o.type);

    const strippedCount =
      (routeOverrides.length - validRouteOverrides.length) +
      (componentOverrides.length - validComponentOverrides.length) +
      (elementOverrides.length - validElementOverrides.length);
    if (strippedCount > 0) {
      console.warn(`generateOverrides: stripped ${strippedCount} override(s) with no type/route before save`);
    }

    return {
      routeOverrides: validRouteOverrides,
      componentOverrides: validComponentOverrides,
      elementOverrides: validElementOverrides
    }
  }

  id(): string {
    return `${Math.random().toString(36).substr(2)}${Math.random().toString(36).substr(2)}${Math.random().toString(36).substr(2)}`
  }

  // Deep clone a tree item, assigning fresh IDs throughout and re-leveling it to
  // `targetLevel`. Used when dropping a source route into the target pane.
  cloneTemplate(item: TreeItem, targetLevel: number): TreeItem {
    const newId = this.id();
    const newItem: TreeItem = {
      ...item,
      id: newId,
      fromId: item.id,
      level: targetLevel,
      expanded: false,
      updatedProperties: item.item?.properties,
      isNew: true,
      children: [],
      item: this.cloneModelForItem(item, newId)
    };

    if (item.children && item.children.length > 0) {
      // `level` is semantic (Route/Component/Element/SubComponent/...), not tree depth.
      // Preserve each descendant's original level, shifting only by however much the
      // dropped root item itself was re-leveled.
      const levelDelta = targetLevel - item.level;
      newItem.children = item.children.map(child => this.cloneTemplateRecursive(child, child.level + levelDelta, newId, levelDelta));
    }

    return newItem;
  }

  private cloneTemplateRecursive(item: TreeItem, level: number, parentComponentId?: string, levelDelta: number = 0): TreeItem {
    const newId = this.id();
    const newItem: TreeItem = {
      ...item,
      id: newId,
      fromId: item.id,
      level: level,
      updatedProperties: item.item?.properties,
      isNew: true,
      children: [],
      item: this.cloneModelForItem(item, newId, parentComponentId)
    };

    if (item.children && item.children.length > 0) {
      const nextParentComponentId = item.level === TreeLevel.Component || item.level === TreeLevel.SubComponent
        ? newId
        : parentComponentId;
      newItem.children = item.children.map(child => this.cloneTemplateRecursive(child, child.level + levelDelta, nextParentComponentId, levelDelta));
    }

    return newItem;
  }

  private cloneModelForItem(item: TreeItem, newId: string, parentComponentId?: string): any {
    if (!item.item) {
      return item.item;
    }

    const clonedModel = JSON.parse(JSON.stringify(item.item as any));

    if (item.level === TreeLevel.Route) {
      clonedModel.routeId = newId;
    }

    if (item.level === TreeLevel.Component) {
      clonedModel.componentId = newId;
    }

    if (item.level === TreeLevel.SubComponent) {
      clonedModel.componentId = newId;
      if (parentComponentId) {
        clonedModel.parentComponentId = parentComponentId;
      }
    }

    if (item.level === TreeLevel.Element || item.level === TreeLevel.SubComponentElement) {
      clonedModel.elementId = newId;
    }

    return clonedModel;
  }
}
